import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import {callTargetedAction, getStoreByName, setUpStore} from 'client/framework';
import radioButtonGroupActions from './RadioButtonGroup.actions';
import RadioButtonGroupStore from './RadioButtonGroup.store';
import './radio-btn-group.scss';

// Say goodbye to a world without button groups, and say hello to RadioButtonGroup!
//
// First pass it a storeName, like this
// const studentViewButtonGroupStoreName = 'studentViewButtonGroupStoreName';
//
// Pass it a list of labels and values, like this:
// const sampleData = fromJS([
//   {
//     label: 'Class',
//     value: 'class'
//   },
//   {
//     label: 'Student',
//     value: 'student'
//   },
//   {
//     label: 'Group',
//     value: 'group'
//   }
// ]);
//
//  And in the wild, it looks like this:
//
//  <RadioButtonGroup
//    data={sampleData}
//    defaultValue='class'
//    storeName={studentViewButtonGroupStoreName}
//  />
//
// About the props:
//
// storeName: PropTypes.string (required)
// data: ImmutablePropTypes.list (required)
//    Data which will be used to create the button group. When a button is clicked,
//    its corresponding value from the List will be set as 'selectedItem' in the component store
// name: PropTypes.string (required)
//    The chosen name links the radios together as related HTML elements, and links the labels
//    to the inputs to act as the buttons.
// defaultValue: PropTypes.any
//    If passed, prop will be set as the selected value on componentDidMount. You definitely
//    want to include the same value as a value in the original List
// onChange: PropTypes.func
//    If passed, function will run when the current value of the selected item is different
//    than the value which the selected item is about to be set to
// disabled: PropTypes.bool
//    Disables the button group and adds a CSS class.

export default class RadioButtonGroup extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    color: PropTypes.string,
    data: ImmutablePropTypes.list.isRequired,
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    storeName: PropTypes.string.isRequired
  };

  static defaultProps = {
    className: '',
    disabled: false,
    onChange: () => {}
  };

  componentDidMount() {
    const defaultValue = this.props.defaultValue;
    if (defaultValue) {
      callTargetedAction({
        name: radioButtonGroupActions.SET_SELECTED_ITEM,
        payload: defaultValue,
        targetStore: this.props.storeName
      });
    }
  }

  componentWillUnmount() {
    callTargetedAction({
      name: radioButtonGroupActions.RESET_STORE,
      targetStore: this.props.storeName
    });
  }

  getStore() {
    return setUpStore(RadioButtonGroupStore, this.props.storeName);
  }

  handleClick(value) {
    if (this.props.disabled || value === this.getStore().getSelectedItem()) {
      return;
    }

    callTargetedAction({
      name: radioButtonGroupActions.SET_SELECTED_ITEM,
      payload: value,
      targetStore: this.props.storeName
    });

    this.props.onChange(value);
  }

  handleKeyPress(e, value) {
    if (e.which === 13) {
      // Enter key
      this.handleClick(value);
    }
  }

  generateOptions(options) {
    const name = this.props.name;
    const isDisabled = this.props.disabled;
    const isGray = this.props.color === 'gray';
    const selectedValue = this.getStore().getSelectedItem();
    return options.map((option, key) => {
      const label = option.get('label');
      const value = option.get('value');
      const isSelected = selectedValue === value;
      const radioButtonClasses = classnames('radio-btn-group__btn', {
        'radio-btn-group__btn--selected': isSelected,
        'radio-btn-group__btn--disabled': isDisabled,
        'radio-btn-group__btn--gray': isGray && isSelected
      });
      return (
        <label
          className={radioButtonClasses}
          htmlFor={value}
          key={key}
          tabIndex='0'
          onClick={() => this.handleClick(value)}
          onKeyPress={(e) => {
            this.handleKeyPress(e, value);
          }}
        >
          <input
            checked={isSelected}
            className='radio-btn__radio-input'
            disabled={isDisabled}
            id={value}
            name={name}
            tabIndex='1'
            type='radio'
            value={value}
          />
          {label}
        </label>
      );
    });
  }

  render() {
    const options = this.props.data;
    const buttons = this.generateOptions(options);
    return <div className={classnames('radio-btn-group', this.props.className)}>{buttons}</div>;
  }
}
